import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '../api/axios';
import { FaArrowLeft, FaFileInvoiceDollar, FaUser, FaInfoCircle, FaMoneyBillWave,FaPrint } from 'react-icons/fa';

export default function InvoiceDetail() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/facturas/${id}`);
      setInvoice(response.data);
    } catch (err) {
      setError('Error al cargar los detalles de la factura. Por favor, intente de nuevo.');
      console.error('Error fetching invoice details:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <Link to="/home/facturas" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800">
          <FaArrowLeft className="mr-2" /> Volver a la lista de facturas
        </Link>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded" role="alert">
          <p className="font-bold">Advertencia</p>
          <p>No se encontró la factura</p>
        </div>
        <Link to="/home/facturas" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800">
          <FaArrowLeft className="mr-2" /> Volver a la lista de facturas
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
      <Link to="/home/facturas" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors duration-200">
        <FaArrowLeft className="mr-2" /> Volver a la lista de facturas
      </Link>
      <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-3xl font-bold flex items-center">
              <FaFileInvoiceDollar className="mr-4 text-4xl" />
              Factura #{invoice.codigo}
            </h2>
            <div className="space-x-2">
              <button onClick={() => window.print()} className="bg-white text-blue-600 hover:bg-blue-100 transition-colors duration-200 py-2 px-4 rounded-full inline-flex items-center">
                <FaPrint className="mr-2" /> Imprimir
              </button>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <FaInfoCircle className="mr-2 text-blue-600" /> Información de la Factura
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <p className="text-gray-700"><strong className="text-gray-900">Fecha de Factura:</strong> {new Date(invoice.fechaFactura).toLocaleDateString()}</p>
                <p className="text-gray-700"><strong className="text-gray-900">Fecha de Registro:</strong> {new Date(invoice.fechaRegistro).toLocaleDateString()}</p>
                <p className="text-gray-700"><strong className="text-gray-900">Número:</strong> {invoice.numero}</p>
                <p className="text-gray-700"><strong className="text-gray-900">Año del Ejercicio:</strong> {invoice.ejercicio?.anio || 'No disponible'}</p>
                <p className="text-gray-700"><strong className="text-gray-900">Código:</strong> {invoice.codigo}</p>
              </div>
              <p className="text-gray-700 mt-4"><strong className="text-gray-900">Descripción:</strong> {invoice.descripcion}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <FaMoneyBillWave className="mr-2 text-green-600" /> Detalles Financieros
              </h3>
              <div className="space-y-2">
                <p className="text-gray-700 flex justify-between">
                  <span>Total con IVA:</span>
                  <span className="text-green-600 font-bold text-xl">{parseFloat(invoice.totalConIva).toFixed(2)} €</span>
                </p>
                <p className="text-gray-700 flex justify-between">
                  <span>Base Imponible:</span>
                  <span>{parseFloat(invoice.baseImponible).toFixed(2)} €</span>
                </p>
                <p className="text-gray-700 flex justify-between">
                  <span>Total IVA:</span>
                  <span>{parseFloat(invoice.totalIva).toFixed(2)} €</span>
                </p>
                <p className="text-gray-700 flex justify-between">
                  <span>Total Descuento:</span>
                  <span>{parseFloat(invoice.totalDescuento).toFixed(2)} €</span>
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaUser className="mr-2 text-purple-600" /> Información del Cliente
            </h3>
            <p className="text-gray-700"><strong className="text-gray-900">Documento del Tercero:</strong> {invoice.terceroId}</p>
            <p className="text-gray-700"><strong className="text-gray-900">Nombre del Tercero:</strong> {invoice.tercero?.nombre || 'No disponible'}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaFileInvoiceDollar className="mr-2 text-yellow-600" /> Detalles de la Factura
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left">Descripción</th>
                    <th className="px-4 py-2 text-right">Cantidad</th>
                    <th className="px-4 py-2 text-right">IVA (%)</th>
                    <th className="px-4 py-2 text-right">Descuento</th>
                    <th className="px-4 py-2 text-right">Importe</th>
                    <th className="px-4 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.detallesFactura.map((detalle, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 py-2">{detalle.descripcion}</td>
                      <td className="px-4 py-2 text-right">{detalle.cantidadProducto}</td>
                      <td className="px-4 py-2 text-right">{detalle.iva}%</td>
                      <td className="px-4 py-2 text-right">{parseFloat(detalle.descuento).toFixed(2)} €</td>
                      <td className="px-4 py-2 text-right">{parseFloat(detalle.importe).toFixed(2)} €</td>
                      <td className="px-4 py-2 text-right">{parseFloat(detalle.totalEjercicio).toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="font-bold bg-gray-200">
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-right">Total:</td>
                    <td className="px-4 py-2 text-right text-green-600">{parseFloat(invoice.totalConIva).toFixed(2)} €</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}